import { redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'

import { isStaffAdmin, loadMe } from './me.ts'
import { BACKOFFICE_ROLES, hasAnyRole, rolesOf, type Role } from '@volee/api-client'

// The per-role route guard (docs/design/frontend-v1.md §4). **UX, not security**: the backend
// protects the data. It runs in `beforeLoad`, so nothing protected is fetched or flashed.

export interface GuardRequirements {
  readonly queryClient: QueryClient
  /** The roles allowed through. Empty = being authenticated is enough. */
  readonly requiredRoles?: readonly Role[]
  /** Where to return after signing in. */
  readonly currentPath: string
}

/** Throws a redirect when the user cannot be on this route: no session → `/sign-in` with a
 * `returnTo`, session but no role → `/forbidden`, because the login would be a loop. */
export async function guardRoute({
  queryClient,
  requiredRoles = [],
  currentPath,
}: GuardRequirements): Promise<void> {
  const state = await loadMe(queryClient)

  // Path literals, not route objects: the routes import this guard, so the other direction
  // would be an import cycle.
  if (state.kind === 'anonymous') {
    // `reason` explains a bounce from a page the user was going to. Landing on `/` is where
    // Cognito drops them after a sign-out, and a refusal notice there answers nothing.
    const wasGoingSomewhere = currentPath !== '/'
    throw redirect({
      to: '/sign-in',
      search: wasGoingSomewhere ? { returnTo: currentPath, reason: 'required' } : { returnTo: '/' },
    })
  }

  // A verified token cannot be sent to the login: signing in again produces the same refusal.
  // The reason travels as a shareable hint; `/forbidden` believes the query, not the param.
  if (state.kind === 'refused') {
    throw redirect({ to: '/forbidden', search: { reason: state.reason } })
  }

  if (!hasAnyRole(rolesOf(state.me), requiredRoles)) {
    throw redirect({ to: '/forbidden', search: { reason: 'no-role' } })
  }
}

/** The staff-only `beforeLoad` every back-office route shares. One line at the route. */
export function requireBackoffice(args: {
  context: { queryClient: QueryClient }
  location: { href: string }
}): Promise<void> {
  return guardRoute({
    queryClient: args.context.queryClient,
    requiredRoles: BACKOFFICE_ROLES,
    currentPath: args.location.href,
  })
}

/** `beforeLoad` for the routes only an admin may open. Why not `requiredRoles`:
 * apps/admin-panel/docs/pattern-di-una-feature.md § «L'invito, e le sue sei decisioni». */
export async function requireStaffAdmin(args: {
  context: { queryClient: QueryClient }
  location: { href: string }
}): Promise<void> {
  // The back office first, so a visitor with no session lands on the login and not on a
  // refusal. The second `loadMe` is free: the key has just resolved.
  await requireBackoffice(args)
  if (!isStaffAdmin(await loadMe(args.context.queryClient))) {
    throw redirect({ to: '/forbidden', search: { reason: 'admin-required' } })
  }
}
